import { ItemRequirementLine } from '@/ui/components/item-requirement-line';
import { LifeResourceIcon } from '@/ui/components/life-resource-icon';
import { LifeSkillPanel } from '@/ui/components/life-skill-panel';
import { FlairPanel } from '@/ui/components/flair-panel';
import { getLifeSkills, getProfession, requestLifeSkills, sendLifeAction, subscribeLifeSkills } from '@/life-skills';
import type { CharacterDetails, EifRecord } from 'eolib';
import { StatId } from 'eolib';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';
import type { CharacterTab } from '@/controllers';
import { EquipmentSlot } from '@/equipment';
import { playSfxById, SfxId } from '@/sfx';
import { Button, ItemIcon, QuestBookList, Tabs } from '@/ui/components';
import { UI_PANEL_BORDER, UI_STICKY_BG } from '@/ui/consts';
import { useCharacterInfo, useClient, useLocale } from '@/ui/context';
import { useBackdropBlur, useItemDrag, usePlayerStats } from '@/ui/in-game';
import { capitalize, getItemMeta } from '@/utils';
import { DialogBase } from './dialog-base';
import {masteryView,subscribeMastery,requestMastery} from '@/spell-mastery';

type CharacterDialogTab = CharacterTab | 'flair';

const EQUIP_CELL = 23;

function getEquipTooltipLines(record: EifRecord, itemId: number): string[] {
  return [record.name, ...getItemMeta(record, itemId)];
}

type SlotConfig = {
  key: string;
  label: string;
  itemId: number;
  slot: EquipmentSlot;
  gridColumn: string;
  gridRow: string;
};

function EquipSlot({ label, itemId, slot, gridColumn, gridRow }: SlotConfig) {
  const client = useClient();
  const info = useCharacterInfo();
  const { startDrag, cancelDrag } = useItemDrag();
  const record = itemId ? client.getEifRecordById(itemId) : null;
  const isOwnCharacter = info?.details.playerId === client.playerId;
  const [, rerenderForge] = useState(0);
  useEffect(() => {
    const update = () => rerenderForge(n => n + 1);
    window.addEventListener('forge-items-updated', update);
    return () => window.removeEventListener('forge-items-updated', update);
  }, []);
  const enhancement = isOwnCharacter ? client.forgeController.getItemEnhancement(itemId, true) : undefined;
  const tooltipLines = record ? (enhancement ? [enhancement.display_name,
    ...enhancement.elements.map(e => `${e.element} +${e.power}`), ...enhancement.stats, ...(enhancement.totals ?? []),
    ...getItemMeta(record, itemId, enhancement)] : getEquipTooltipLines(record, itemId)) : null;
  const [touchTooltip, setTouchTooltip] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);

  // Clear touch tooltip when touching outside this slot
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (!slotRef.current?.contains(e.target as Node)) {
        setTouchTooltip(false);
      }
    };
    window.addEventListener('pointerdown', handler);
    return () => window.removeEventListener('pointerdown', handler);
  }, []);

  const doUnequip = () => {
    client.inventoryController.unequipItem(slot);
  };

  const onPointerDown = (e: PointerEvent) => {
    if (!record || !itemId) return;
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    e.preventDefault();

    if (e.pointerType === 'touch') {
      setTouchTooltip(true);
    }

    if (!isOwnCharacter) return;

    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Long-press on touch: cancel drag and unequip
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    if (e.pointerType === 'touch') {
      longPressTimer = setTimeout(() => {
        longPressTimer = null;
        cancelDrag();
        setTouchTooltip(false);
        doUnequip();
      }, 700);
    }

    playSfxById(SfxId.InventoryPickup);

    startDrag({
      element: e.currentTarget as Element,
      info: {
        source: 'equipment',
        itemId,
        equipSlot: slot,
        pointerId: e.pointerId,
        ghostX: e.clientX,
        ghostY: e.clientY,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        graphicId: record.graphicId,
        ghostWidth: rect.width,
        ghostHeight: rect.height,
      },
      onResolve: (result) => {
        if (longPressTimer) {
          clearTimeout(longPressTimer);
          longPressTimer = null;
        }
        if (result.type === 'forge') {
          client.forgeController.dropItem(result.slot, itemId);
          return;
        }
        if (result.type !== 'cancelled') {
          doUnequip();
        }
      },
    });
  };

  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    if (!record || !itemId || !isOwnCharacter) return;
    doUnequip();
  };

  return (
    <div
      ref={slotRef}
      data-equip-slot={isOwnCharacter ? slot : undefined}
      class={`group relative flex items-center justify-center rounded border ${itemId ? 'border-primary/30 bg-primary/5' : UI_PANEL_BORDER}${isOwnCharacter && itemId ? 'cursor-grab' : ''}`}
      style={{ gridColumn, gridRow }}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
    >
      {record ? (
        <ItemIcon
          graphicId={record.graphicId}
          upgradeLevel={enhancement?.upgrade_level ?? 0}
          alt={record.name}
          class='pointer-events-none h-full w-full object-contain'
        />
      ) : (
        <span class='pointer-events-none text-center text-[7px] text-base-content/40 leading-tight'>
          {label}
        </span>
      )}
      {tooltipLines && tooltipLines.length > 0 && (
        <div
          class={`pointer-events-none absolute top-0 left-full z-50 ml-1 w-max max-w-40 rounded bg-base-300 px-2 py-1 text-xs shadow-lg ${touchTooltip ? 'block' : 'hidden group-hover:block'}`}
        >
          {tooltipLines.map((line, i) => (
            <div key={i} class={i === 0 ? 'font-semibold' : 'opacity-70'}>
              <ItemRequirementLine line={line} item={record} client={client} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaperdollTab() {
  const info = useCharacterInfo();
  const { locale } = useLocale();
  const client = useClient();
  const [,refreshProfession] = useState(0);
  useEffect(()=>{
    const unsubscribe=subscribeLifeSkills(client,()=>refreshProfession(n=>n+1));
    if(info?.details.playerId===client.playerId)requestLifeSkills(client);
    return unsubscribe;
  },[client,info?.details.playerId]);

  const slots: SlotConfig[] = [
    {
      key: 'hat',
      label: locale.equipment.hat,
      itemId: info?.equipment?.hat ?? 0,
      slot: EquipmentSlot.Hat,
      gridColumn: '3 / span 2',
      gridRow: '1 / span 2',
    },
    {
      key: 'necklace',
      label: locale.equipment.necklace,
      itemId: info?.equipment?.necklace ?? 0,
      slot: EquipmentSlot.Necklace,
      gridColumn: '5 / span 2',
      gridRow: '1 / span 2',
    },
    {
      key: 'weapon',
      label: locale.equipment.weapon,
      itemId: info?.equipment?.weapon ?? 0,
      slot: EquipmentSlot.Weapon,
      gridColumn: '1 / span 2',
      gridRow: '3 / span 3',
    },
    {
      key: 'armor',
      label: locale.equipment.armor,
      itemId: info?.equipment?.armor ?? 0,
      slot: EquipmentSlot.Armor,
      gridColumn: '3 / span 2',
      gridRow: '3 / span 3',
    },
    {
      key: 'shield',
      label: locale.equipment.shield,
      itemId: info?.equipment?.shield ?? 0,
      slot: EquipmentSlot.Shield,
      gridColumn: '5 / span 2',
      gridRow: '3 / span 3',
    },
    {
      key: 'gloves',
      label: locale.equipment.gloves,
      itemId: info?.equipment?.gloves ?? 0,
      slot: EquipmentSlot.Gloves,
      gridColumn: '1 / span 2',
      gridRow: '6 / span 2',
    },
    {
      key: 'belt',
      label: locale.equipment.belt,
      itemId: info?.equipment?.belt ?? 0,
      slot: EquipmentSlot.Belt,
      gridColumn: '3 / span 2',
      gridRow: '6',
    },
    {
      key: 'ring1',
      label: locale.equipment.ring1,
      itemId: info?.equipment?.ring[0] ?? 0,
      slot: EquipmentSlot.Ring1,
      gridColumn: '5',
      gridRow: '6',
    },
    {
      key: 'ring2',
      label: locale.equipment.ring2,
      itemId: info?.equipment?.ring[1] ?? 0,
      slot: EquipmentSlot.Ring2,
      gridColumn: '6',
      gridRow: '6',
    },
    {
      key: 'armlet1',
      label: locale.equipment.armlet1,
      itemId: info?.equipment?.armlet[0] ?? 0,
      slot: EquipmentSlot.Armlet1,
      gridColumn: '5',
      gridRow: '7',
    },
    {
      key: 'armlet2',
      label: locale.equipment.armlet2,
      itemId: info?.equipment?.armlet[1] ?? 0,
      slot: EquipmentSlot.Armlet2,
      gridColumn: '6',
      gridRow: '7',
    },
    {
      key: 'boots',
      label: locale.equipment.boots,
      itemId: info?.equipment?.boots ?? 0,
      slot: EquipmentSlot.Boots,
      gridColumn: '3 / span 2',
      gridRow: '7 / span 2',
    },
    {
      key: 'accessory',
      label: locale.equipment.accessory,
      itemId: info?.equipment?.accessory ?? 0,
      slot: EquipmentSlot.Accessory,
      gridColumn: '2',
      gridRow: '8',
    },
    {
      key: 'bracer1',
      label: locale.equipment.bracer1,
      itemId: info?.equipment?.bracer[0] ?? 0,
      slot: EquipmentSlot.Bracer1,
      gridColumn: '5',
      gridRow: '8',
    },
    {
      key: 'bracer2',
      label: locale.equipment.bracer2,
      itemId: info?.equipment?.bracer[1] ?? 0,
      slot: EquipmentSlot.Bracer2,
      gridColumn: '6',
      gridRow: '8',
    },
  ];

  const infoRows: [string, string][] = [
    [locale.character.labelName, info ? capitalize(info.details.name) : '—'],
    [locale.character.labelHome, info ? info.details.home : '—'],
    ['Profession', info ? getProfession(client,info.details.playerId,info.details.name) : '—'],
    [
      locale.character.labelPartner,
      info?.details.partner ? capitalize(info.details.partner) : '—',
    ],
    [locale.character.labelTitle, info?.details.title || '—'],
    [locale.character.labelGuild, info?.details.guild || '—'],
    [locale.character.labelRank, info?.details.guildRank || '—'],
  ];

  return (
    <div class='flex gap-3'>
      {/* Equipment grid (left) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(6, ${EQUIP_CELL}px)`,
          gridTemplateRows: `repeat(8, ${EQUIP_CELL}px)`,
          gap: 2,
        }}
      >
        {slots.map(({ key, ...rest }) => (
          <EquipSlot key={key} {...rest} />
        ))}
      </div>

      {/* Character info (right) */}
      <div class='flex min-w-0 flex-1 flex-col gap-0.5 text-sm'>
        {infoRows.map(([label, value]) => (
          <div key={label} class='flex justify-between gap-2'>
            <span class='shrink-0 text-primary/60'>{label}</span>
            <span class='min-w-0 truncate font-medium'>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsTab() {
  const client = useClient();
  const [magicView,setMagicView]=useState(()=>masteryView(client));
  useEffect(()=>{
    const off=subscribeMastery(client,()=>setMagicView({...masteryView(client)}));
    const refresh=()=>requestMastery(client);
    requestMastery(client);
    client.inventoryController.subscribeEquipmentChanged(refresh);
    client.statsController.subscribeStatsUpdated(refresh);
    return()=>{off();client.inventoryController.unsubscribeEquipmentChanged(refresh);client.statsController.unsubscribeStatsUpdated(refresh);};
  },[client]);
  const { locale } = useLocale();
  const info = useCharacterInfo();
  const blur = useBackdropBlur();
  const {
    baseStats: base,
    secondaryStats: secondary,
    statPoints,
  } = usePlayerStats();

  const isOwnCharacter = info?.details.playerId === client.playerId;
  const [allocation,setAllocation]=useState('1');
  const [allocating,setAllocating]=useState(false);
  const allocationAmount=allocation==='all'?statPoints:Math.min(statPoints,Number(allocation));
  const canTrain = isOwnCharacter && statPoints > 0 && !allocating && Number.isInteger(allocationAmount) && allocationAmount>0;
  useEffect(()=>{
    const done=()=>setAllocating(false);client.statsController.subscribeStatsUpdated(done);
    return()=>client.statsController.unsubscribeStatsUpdated(done);
  },[client]);
  useEffect(()=>{if(!allocating)return;const timer=setTimeout(()=>setAllocating(false),5000);return()=>clearTimeout(timer);},[allocating]);

  const handleTrain = useCallback(
    (statId: StatId) => {
      if(!canTrain)return;
      const apply=()=>{setAllocating(true);client.statSkillController.trainStat(statId,allocationAmount);};
      if(allocationAmount>1)client.alertController.showConfirm('Apply stat points',`Apply ${allocationAmount} points to ${StatId[statId]}?`,yes=>{if(yes)apply();});
      else apply();
    },
    [client,canTrain,allocationAmount],
  );

  const statPointsLabel = locale.stats.statPoints.replace(
    '{count}',
    String(statPoints),
  );

  const baseRows: [string, number, StatId][] = [
    [locale.stats.labelStr, base.str, StatId.Str],
    [locale.stats.labelInt, base.intl, StatId.Int],
    [locale.stats.labelWis, base.wis, StatId.Wis],
    [locale.stats.labelAgi, base.agi, StatId.Agi],
    [locale.stats.labelCon, base.con, StatId.Con],
    [locale.stats.labelCha, base.cha, StatId.Cha],
  ];

  const derivedRows: [string, string][] = [
    [
      'Physical Damage',
      `${secondary.minDamage.toLocaleString()} - ${secondary.maxDamage.toLocaleString()}`,
    ],
    [locale.stats.labelAccuracy, secondary.accuracy.toLocaleString()],
    [locale.stats.labelEvade, secondary.evade.toLocaleString()],
    [locale.stats.labelArmor, secondary.armor.toLocaleString()],
  ];
  if(isOwnCharacter)derivedRows.splice(1,0,['Magic Damage',magicView.magic_damage?`${magicView.magic_damage.min.toLocaleString()} - ${magicView.magic_damage.max.toLocaleString()}`:'Loading…']);

  return (
    <div class='flex flex-col gap-2 text-sm'>
      {isOwnCharacter && (
        <div
          class={`sticky top-0 z-10 ${UI_PANEL_BORDER} border-b ${UI_STICKY_BG} px-3 py-1.5 ${blur}`}
        >
          <p class='text-center font-medium text-primary text-sm'>
            {statPointsLabel}
          </p>
          <div class='flex flex-wrap items-center justify-center gap-2 py-2'>
            <label class='flex items-center gap-2'>Amount <input aria-label='Stat points to apply' class='input input-sm w-20' type='number' inputMode='numeric' min={1} max={statPoints} value={allocation==='all'?statPoints:allocation} disabled={allocating||statPoints<=0} onInput={e=>setAllocation(e.currentTarget.value)}/></label>
            {[1,10,100,'all'].map(n=><Button key={n} variant={['sm',allocation===String(n)?'primary':'ghost']} disabled={allocating||statPoints<=0} onClick={()=>setAllocation(String(n))}>{n==='all'?'All':n}</Button>)}
          </div>
          {allocating&&<p class='text-center text-xs'>Applying stat points…</p>}
        </div>
      )}
      <div class='flex gap-4'>
        <div class='flex flex-1 flex-col gap-0.5'>
          {baseRows.map(([label, value, statId]) => (
            <div key={label} class='flex items-center justify-between gap-1'>
              <span class='text-primary/60'>{label}</span>
              <span class='flex-1 text-right'>{value.toLocaleString()}</span>
              {isOwnCharacter && (
                <Button
                  variant={['sm', canTrain ? 'primary' : 'disabled']}
                  disabled={!canTrain}
                  onClick={() => handleTrain(statId)}
                >
                  +{Number.isInteger(allocationAmount)&&allocationAmount>0?allocationAmount:0}
                </Button>
              )}
            </div>
          ))}
        </div>
        <div class='flex flex-1 flex-col gap-0.5'>
          {derivedRows.map(([label, value]) => (
            <div key={label} class='flex justify-between'>
              <span class='text-primary/60'>{label}</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BookTab() {
  const client = useClient();

  const [questNames, setQuestNames] = useState<string[]>(
    () => client.questController.bookQuestNames,
  );

  useEffect(() => {
    const handleBookOpened = (names: string[]) => {
      setQuestNames([...names]);
    };
    client.questController.subscribeBookOpened(handleBookOpened);
    return () => {
      client.questController.unsubscribeBookOpened(handleBookOpened);
    };
  }, [client]);

  return <QuestBookList questNames={questNames} />;
}

function LifeSkillsTab() {
  const client = useClient();
  const [view, setView] = useState(() => getLifeSkills(client));
  const [now,setNow]=useState(Date.now());
  useEffect(()=>{const timer=window.setInterval(()=>setNow(Date.now()),250);return()=>clearInterval(timer);},[]);

  useEffect(() => {
    const update = () => setView(getLifeSkills(client));
    const unsubscribe = subscribeLifeSkills(client, update);
    requestLifeSkills(client);
    return unsubscribe;
  }, [client]);

  const skills = view.snapshot?.skills ?? [];
  const action=view.snapshot?.action;
  const fishing=action?.skill==='fishing';
  const bite=!!action&&fishing&&now>=action.ready_at&&now<action.expires_at;
  const escaped=!!action&&fishing&&now>=action.expires_at;

  return (
    <div class='flex flex-col gap-3 text-sm'>
      <p class='text-primary/70'>Life skills progress from level 0 to 99.</p>
      <LifeSkillPanel skills={skills}/>
      {view.snapshot?.tutorial&&<section class='rounded border p-2' aria-label='Life skills tutorial'>
        <h3 class='font-bold'>Learning to Live off the Land — {view.snapshot.tutorial.stage}</h3>
        <p>{view.snapshot.tutorial.instructions}</p>
      </section>}
      <details class='rounded border p-2'>
        <summary class='cursor-pointer font-bold'>Achievements and titles</summary>
        <p>Current title: {view.snapshot?.selected_title||'None'}</p>
        <button type='button' class='btn btn-sm' disabled={view.pending||!view.snapshot?.selected_title} onClick={()=>sendLifeAction(client,'title','clear')}>Clear title</button>
        {(view.snapshot?.achievements??[]).map(a=><div key={a.key} class='mt-2'>
          <div>{a.title} — {a.unlocked?'Unlocked':`${a.progress}/${a.required}`}</div>
          <p class='text-xs'>{a.description}</p>
          <button type='button' class='btn btn-sm' disabled={view.pending||!a.unlocked||view.snapshot?.selected_title===a.title} onClick={()=>sendLifeAction(client,'title',a.key)}>Use title</button>
        </div>)}
      </details>
      <button type='button' class='btn btn-sm' disabled={view.pending} onClick={()=>requestLifeSkills(client)}>Refresh resources</button>
      {(view.snapshot?.action_ready_at ?? 0)>0 && <div class='flex gap-2'>
        <button type='button' class={`btn btn-sm btn-primary ${bite?'animate-pulse':''}`} disabled={view.pending||(!fishing&&now<(view.snapshot?.action_ready_at??0))} onClick={()=>sendLifeAction(client,fishing?'reel':'finish')}>
          {fishing?(escaped?'Fish escaped — reel in':bite?'Fish biting! Reel in!':'Reel in'):
          now<(view.snapshot?.action_ready_at??0)?`Working… ${Math.ceil(((view.snapshot?.action_ready_at??0)-now)/1000)}s`:'Collecting…'}
        </button>
        <button type='button' class='btn btn-sm' disabled={view.pending} onClick={()=>sendLifeAction(client,'cancel')}>Cancel</button>
      </div>}
      {fishing&&<p role='status' aria-live='assertive' class={bite?'text-warning font-bold':''}>{bite?'The net is moving! Reel in before the fish escapes.':escaped?'The fish escaped. Reel in and cast again.':'Net cast. Watch for a bite before reeling in.'}</p>}
      {action?.skill==='cooking'&&<p role='status'>Cooking — {action.remaining??1} remaining. Moving, combat, running out of ingredients or the fire going out stops the batch.</p>}
      {(view.snapshot?.nodes??[]).map(node=><section key={node.id} class='rounded border p-2'>
        <LifeResourceIcon graphic={node.ready_at>now?node.depleted_graphic:node.graphic} name={node.name}/>
        <div>{node.name} — {node.skill} level {node.level}</div>
          <div class='text-xs'>Map position {node.x}, {node.y}. Stand beside it. {node.skill==='fishing'?'Carry a Fishing Net in your inventory; it does not need to be equipped.':node.skill==='woodcutting'?'Equip a Chopper or any axe.':node.graphic==='campfire'?'Use the campfire to cook or start a fire on clear ground.':'No tool is needed.'}</div>
        {node.graphic==='campfire'?<div class='mt-2'>
          <p class='mb-2 text-xs'>Choose food to cook. Each item takes time and may burn; higher Cooking lowers the chance. Burnt food is useless.</p>
          <div class='grid grid-cols-2 gap-2'>
            {(['cook_fish','bake_berries'] as const).map(recipe=><fieldset key={recipe} class='rounded border border-base-content/10 p-2' disabled={view.pending||!!action&&action.skill!=='cooking'||now>=(node.expires_at??0)}>
              <legend>{recipe==='cook_fish'?'Fish · +200 HP':'Berries · +400 TP'}</legend>
              <button type='button' class='btn btn-sm mr-1' onClick={()=>sendLifeAction(client,recipe,node.id)}>Cook 1</button>
              <button type='button' class='btn btn-sm' onClick={()=>sendLifeAction(client,`${recipe}_all`,node.id)}>Cook all</button>
            </fieldset>)}
          </div>
        </div>:<button type='button' class='btn btn-sm mt-1' disabled={view.pending||node.ready_at>now||!!view.snapshot?.action_ready_at} onClick={()=>sendLifeAction(client,'start',node.id)}>
          {node.ready_at>now?`Regrowing ${Math.ceil((node.ready_at-now)/1000)}s`:'Gather'}
        </button>}
      </section>)}
      {view.pending && <p role='status'>Loading life skills…</p>}
      {view.message && <p role='status' class='text-warning'>{view.message}</p>}
      {!skills.length && !view.pending && !view.message && (
        <p class='text-primary/60'>Your life-skill records will appear here.</p>
      )}
    </div>
  );
}

export function CharacterDialog() {
  const { locale } = useLocale();
  const client = useClient();
  const info = useCharacterInfo();

  // Read the pending tab set by socialController before dialog mounted.
  const [activeTab, setActiveTab] = useState<CharacterDialogTab>(
    () => client.socialController.pendingCharacterTab ?? 'paperdoll',
  );

  // Track the last playerId for which each tab's data was fetched so we can
  // detect staleness when switching tabs.
  const lastFetchedBookRef = useRef<number | null>(
    // If we opened via BookReply, the data is already fresh.
    client.socialController.pendingCharacterTab === 'book'
      ? (info?.details.playerId ?? null)
      : null,
  );
  const lastFetchedPaperdollRef = useRef<number | null>(
    // If we opened via PaperdollReply (pendingTab is null), data is fresh.
    client.socialController.pendingCharacterTab === null
      ? (info?.details.playerId ?? null)
      : null,
  );

  // Clear the pending tab after we've consumed it.
  useEffect(() => {
    client.socialController.pendingCharacterTab = null;
  }, [client]);

  // When the dialog is already open and a BookReply arrives, switch to book tab.
  useEffect(() => {
    const onBookOpened = ({ details }: { details: CharacterDetails }) => {
      lastFetchedBookRef.current = details.playerId;
      setActiveTab('book');
    };
    client.socialController.subscribeBookOpened(onBookOpened);
    return () => client.socialController.unsubscribeBookOpened(onBookOpened);
  }, [client]);

  // When the dialog is already open and a PaperdollReply arrives, switch to paperdoll tab.
  useEffect(() => {
    const onPaperdollOpened = ({
      details,
    }: {
      details: { playerId: number };
    }) => {
      lastFetchedPaperdollRef.current = details.playerId;
      setActiveTab('paperdoll');
    };
    client.socialController.subscribePaperdollOpened(onPaperdollOpened);
    return () =>
      client.socialController.unsubscribePaperdollOpened(onPaperdollOpened);
  }, [client]);

  const handleTabSelect = (tabId: CharacterDialogTab) => {
    const playerId = info?.details.playerId;
    if (playerId !== undefined) {
      if (
        tabId === 'paperdoll' &&
        (info?.equipment === undefined ||
          lastFetchedPaperdollRef.current !== playerId)
      ) {
        client.socialController.requestPaperdoll(playerId);
        lastFetchedPaperdollRef.current = playerId;
      } else if (tabId === 'book' && lastFetchedBookRef.current !== playerId) {
        client.socialController.requestBook(playerId);
        lastFetchedBookRef.current = playerId;
      }
    }
    setActiveTab(tabId);
  };

  const TABS = useMemo(() => {
    if (info?.details.playerId === client.playerId) {
      return [
        { id: 'paperdoll', label: locale.character.tabPaperdoll },
        { id: 'flair', label: 'Flair' },
        { id: 'stats', label: locale.character.tabStats },
        { id: 'life-skills', label: 'Life skills' },
        { id: 'book', label: locale.character.tabBook },
      ] as const;
    }

    return [
      { id: 'paperdoll', label: locale.character.tabPaperdoll },
      { id: 'book', label: locale.character.tabBook },
    ] as const;
  }, [info, client, locale]);

  return (
    <DialogBase id='character' title={locale.character.title} size='md'>
      <div class='flex flex-col gap-3'>
        <Tabs
          items={TABS as unknown as { id: string; label: string }[]}
          activeId={activeTab}
          onSelect={(id) => handleTabSelect(id as CharacterDialogTab)}
          style='border'
          size='sm'
        />
        {activeTab === 'paperdoll' && <PaperdollTab />}
        {activeTab === 'flair' && info?.details.playerId === client.playerId && <FlairPanel />}
        {activeTab === 'stats' && <StatsTab />}
        {activeTab === 'life-skills' && <LifeSkillsTab />}
        {activeTab === 'book' && <BookTab />}
      </div>
    </DialogBase>
  );
}
